import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { SUPPORTED_CURRENCIES } from '../utils/currencies';
import { supabase } from '../lib/supabase';

// DEGIRO localises headers (EN/NL/DE). The Account statement export also has
// ISIN and Order Id, but no quantity or price column.
export const looksLikeDegiroTransactions = (text = '') => {
  const header = (text.replace(/^\uFEFF/, '').split(/\r?\n/, 1)[0] || '')
    .split(',')
    .map((cell) => cell.trim().replace(/^"|"$/g, '').toLowerCase());
  const has = (...names) => header.some((cell) => names.includes(cell));
  return has('isin') && has('order id', 'order-id') && has('quantity', 'aantal', 'anzahl') && has('price', 'koers', 'kurs');
};

export const getImportUploadIntent = (assetKind = 'crypto') => (
  assetKind === 'stock'
    ? { assetKind: 'stock', importSource: 'degiro_transactions' }
    : { assetKind: 'crypto', importSource: undefined }
);

const ImportTransactionsModal = ({ isOpen, onClose, portfolioId, onSuccess, defaultTab = 'file', initialCSV = '', assetKind = 'crypto' }) => {
  const isStock = assetKind === 'stock';
  const [activeTab, setActiveTab] = useState(isStock ? 'file' : defaultTab); // 'file' | 'paste'
  const [selectedFile, setSelectedFile] = useState(null);
  const [csvContent, setCsvContent] = useState(initialCSV || '');
  const [dataInput, setDataInput] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Reset state when modal opens or defaults change
  useEffect(() => {
    if (isOpen) {
      setIsComplete(false);
      setActiveTab(isStock ? 'file' : defaultTab);
      setCsvContent(initialCSV || '');
      setDataInput('');
      setSelectedFile(null);
    }
  }, [isOpen, defaultTab, initialCSV, isStock]);

  // Handle ESC key press
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const onDrop = React.useCallback((acceptedFiles) => {
    const file = acceptedFiles && acceptedFiles[0];
    if (file) {
      setSelectedFile(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    maxFiles: 1,
    maxSize: 2 * 1024 * 1024, // 2 MB
    disabled: isImporting,
    accept: isStock
      ? {
          'text/csv': ['.csv', '.CSV'],
          'application/vnd.ms-excel': ['.csv', '.CSV'],
        }
      : {
          'text/*': ['.txt', '.csv', '.TXT', '.CSV'],
          'application/vnd.ms-excel': ['.csv', '.CSV'],
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx', '.XLSX'],
        },
    onDropRejected: (fileRejections) => {
      const tooLarge = fileRejections?.some((r) => r.errors?.some((e) => e.code === 'file-too-large'));
      if (tooLarge) {
        toast.error('File is too large. Maximum allowed size is 2 MB.');
        return;
      }
      toast.error(isStock ? 'Choose the CSV file exported from DEGIRO.' : 'Only text files (.txt, .csv) or Excel files (.xlsx) up to 2 MB are allowed.');
    },
  });

  const handleImport = async () => {
    if (!portfolioId || isImporting) return;

    let inputText = '';
    try {
      setIsImporting(true);
      if (activeTab === 'file') {
        if (!selectedFile) {
          toast.error('Please select a file');
          setIsImporting(false);
          return;
        }
        inputText = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = () => reject(new Error('Failed to read file'));
          reader.readAsText(selectedFile);
        });
        if (isStock && !looksLikeDegiroTransactions(inputText)) {
          toast.error('This is not a DEGIRO Transactions export. In DEGIRO, export Inbox → Transactions as CSV (not the Account statement).');
          setIsImporting(false);
          return;
        }
      } else {
        const txt = (dataInput || csvContent || '').trim();
        if (!txt) {
          toast.error('Please paste some data');
          setIsImporting(false);
          return;
        }
        inputText = txt;
      }

      setIsUploading(true);
      const uploadIntent = getImportUploadIntent(assetKind);
      // Build request to Edge Function
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        toast.error('You must be signed in to import');
        setIsUploading(false);
        setIsImporting(false);
        return;
      }
      const funcUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/handle_user_uploads`;

      let response;
      if (activeTab === 'file') {
        const form = new FormData();
        form.append('file', selectedFile);
        form.append('kind', 'transactions_csv');
        form.append('portfolio_id', portfolioId);
        form.append('asset_kind', uploadIntent.assetKind);
        if (uploadIntent.importSource) {
          form.append('import_source', uploadIntent.importSource);
        }
        response = await fetch(funcUrl, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });
      } else {
        const body = {
          text: inputText,
          filename: 'pasted.txt',
          kind: 'transactions_text',
          portfolioId,
          assetKind: uploadIntent.assetKind,
        };
        response = await fetch(funcUrl, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });
      }

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const msg = err?.error || `Upload failed (${response.status})`;
        toast.error(msg);
        setIsUploading(false);
        setIsImporting(false);
        return;
      }
      const payload = await response.json();
      const jobId = payload?.jobId;
      if (!jobId) {
        toast.error('Failed to start import job');
        setIsUploading(false);
        setIsImporting(false);
        return;
      }
      setIsUploading(false);
      // Job status updates will be delivered via Supabase Realtime (JobNotificationsContext)
      toast.success('Import started. We will notify you when it needs review.');
      onClose();
    } catch (error) {
      console.error('Import start error:', error);
      toast.error(error.message || 'Failed to start import');
    } finally {
      setIsImporting(false);
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white dark:bg-gray-800">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{isStock ? 'Import stock transactions' : 'Import Transactions'}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          {!isComplete && !isStock && (
            <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab('file')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'file'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Upload File
                </button>
                <button
                  onClick={() => setActiveTab('paste')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'paste'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Paste Data
                </button>
              </nav>
            </div>
          )}

          {/* Tab Content */}
          {!isComplete && activeTab === 'file' && (
            <div className="mb-4">
              <div
                {...getRootProps({
                  className:
                    `flex flex-col items-center justify-center w-full p-6 border-2 border-dashed rounded-md transition-colors ` +
                    `${isDragActive ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'} ` +
                    `${isImporting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-blue-400'}`,
                })}
              >
                <input {...getInputProps()} />
                <svg
                  className="w-8 h-8 text-gray-500 dark:text-gray-300 mb-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <p className="text-sm text-gray-700 dark:text-gray-200 text-center">
                  {isDragActive
                    ? 'Drop your file here…'
                    : isStock
                      ? 'Drag & drop your stock transactions CSV here, or click to browse'
                      : 'Drag & drop CSV or Excel file here, or click to browse'}
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{isStock ? 'CSV file up to 2 MB' : 'CSV or Excel files (.csv, .xlsx) up to 2 MB'}</p>
              </div>
              {selectedFile && (
                <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                  <p className="text-sm text-green-800 dark:text-green-200">
                    Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </p>
                </div>
              )}
            </div>
          )}

          {!isComplete && activeTab === 'paste' && (
            <div className="mb-4">
              <textarea
                value={dataInput}
                onChange={(e) => setDataInput(e.target.value)}
                disabled={isImporting}
                placeholder="Paste CSV/Excel data here..."
                rows={10}
                className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          )}
          {/* Chunk Size fixed to 50; input removed */}

          {/* Completion message */}
          {isComplete && (
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-md text-sm text-gray-700 dark:text-gray-300">
              Import completed. You can review results in your portfolio.
            </div>
          )}

          {/* Local progress indicator */}
          {isImporting && (
            <div className="mb-4 text-sm text-gray-700 dark:text-gray-300">Preparing and submitting your data…</div>
          )}

          {/* No inline warnings in unified background flow */}

          {!isComplete && isStock && (
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-md text-sm text-gray-700 dark:text-gray-300 space-y-2">
              <p>
                <strong>How to export:</strong> in DEGIRO, open <em>Inbox → Transactions</em>, choose the date range and click{' '}
                <em>Export → CSV</em>. The Account statement export is not supported.
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Each row is matched to a stock by its ISIN and exchange and booked to a &quot;DEGIRO&quot; location. Fees include
                DEGIRO&apos;s AutoFX charge. You can review every row before anything is imported, and rows you already imported
                are flagged as duplicates.
              </p>
            </div>
          )}

          {/* CSV format + AI parsing guidance */}
          {!isComplete && !isStock && (
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                <strong>CSV/Excel Format:</strong> DateTime, Ticker, CoingeckoId, Type, Price, Quantity, Fees, Currency, Notes
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Type must be: BUY, SELL, TRANSFER_IN, or TRANSFER_OUT
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Currency and Notes are optional. If Currency is omitted, the portfolio currency is used by default.
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Allowed currencies: {SUPPORTED_CURRENCIES.join(', ')}.
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-3">
                Upload CSV or Excel files, or paste CSV-style tables (comma/semicolon/tab delimited); the system will detect fields and parse
                transactions. You'll be able to review records before import.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            {!isImporting && isComplete && (
              <button
                onClick={() => {
                  onClose();
                  setSelectedFile(null);
                  setCsvContent('');
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                Close & Finish
              </button>
            )}
            {!isImporting && isComplete && (
              <button
                onClick={() => {
                  setActiveTab('file');
                  setSelectedFile(null);
                  setCsvContent('');
                  setIsComplete(false);
                }}
                className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                New Import
              </button>
            )}
            {!isComplete && (
              <button
                onClick={onClose}
                disabled={isImporting}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            )}
            {!isComplete && (
              <button
                onClick={handleImport}
                disabled={
                  isImporting ||
                  isUploading ||
                  (activeTab === 'file' && !selectedFile) ||
                  (activeTab === 'paste' && !(dataInput.trim() || csvContent.trim()))
                }
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isImporting || isUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    <span>{isImporting ? 'Preparing…' : 'Uploading…'}</span>
                  </>
                ) : (
                  'Import'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportTransactionsModal;
